import { backUrl } from "../utils/backURL";
import { apiFetch } from "../utils/apiFetch";

export const getMantisAgents = async (token) => {
  try {
    const response = await apiFetch(`${backUrl}/mantis-agents`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching mantis agents:", error);
    throw error;
  }
};

export const getMantisAgentsWithDecryptedData = async (token) => {
  try {
    const response = await apiFetch(`${backUrl}/mantis-agents/decrypted`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching mantis agents with decrypted data:", error);
    throw error;
  }
};

export const getMantisAgentByIdWithDecryptedData = async (token, agentId) => {
  try {
    const response = await apiFetch(`${backUrl}/mantis-agents/${agentId}/decrypted`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching mantis agent with decrypted data:", error);
    throw error;
  }
};

export const getMantisAgentById = async (token, agentId) => {
  try {
    const response = await apiFetch(`${backUrl}/mantis-agents/${agentId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching mantis agent:", error);
    throw error;
  }
};

/**
 * Polls for agent deployment status until completion or failure
 * @param {string} token - Authentication token
 * @param {string} agentId - Agent MongoDB _id
 * @param {number} maxAttempts - Maximum number of polling attempts (default: 60)
 * @param {number} intervalMs - Polling interval in milliseconds (default: 5000 = 5 seconds)
 * @returns {Promise<Object>} Final agent status
 */
export const pollAgentDeploymentStatus = async (
  token,
  agentId,
  maxAttempts = 60,
  intervalMs = 5000
) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const result = await getMantisAgentById(token, agentId);

      if (!result.success || !result.data) {
        return {
          success: false,
          error: "Failed to fetch agent status"
        };
      }

      const agent = result.data;
      const deploymentStatus = agent.deploymentStatus;

      if (deploymentStatus === "completed") {
        return {
          success: true,
          data: {
            status: "completed",
            agentId: agent.agentId,
            message: "Deployment completed successfully"
          }
        };
      }

      if (deploymentStatus === "failed") {
        return {
          success: false,
          error: "Deployment failed"
        };
      }

      // If still pending or in_progress, wait and poll again
      if (deploymentStatus === "pending" || deploymentStatus === "in_progress" || !deploymentStatus) {
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
          continue;
        } else {
          return {
            success: false,
            error: "Deployment timed out. It may still be processing."
          };
        }
      }

      // Unknown status
      return {
        success: false,
        error: `Unknown deployment status: ${deploymentStatus}`
      };
    } catch (error) {
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        continue;
      }
      return {
        success: false,
        error: error.message || "Failed to poll agent status"
      };
    }
  }

  return {
    success: false,
    error: "Polling timeout reached"
  };
};

/**
 * Polls for agent deployment status with UI update callback
 * @param {string} token - Authentication token
 * @param {string} agentId - Agent MongoDB _id
 * @param {Function} onUpdate - Callback function called on each poll with updated agent data
 * @param {number} maxAttempts - Maximum number of polling attempts (default: 60)
 * @param {number} intervalMs - Polling interval in milliseconds (default: 5000 = 5 seconds)
 * @returns {Promise<Object>} Final agent status
 */
export const pollAgentDeploymentStatusWithUpdates = async (
  token,
  agentId,
  onUpdate,
  maxAttempts = 60,
  intervalMs = 5000
) => {
  console.log(`[Polling] Starting to poll agent ${agentId} (max ${maxAttempts} attempts, interval ${intervalMs}ms)`);
  
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      console.log(`[Polling] Attempt ${attempt + 1}/${maxAttempts} for agent ${agentId}`);
      const result = await getMantisAgentById(token, agentId);

      if (!result.success || !result.data) {
        console.error(`[Polling] Failed to fetch agent ${agentId}:`, result);
        return {
          success: false,
          error: "Failed to fetch agent status"
        };
      }

      const agent = result.data;
      const deploymentStatus = agent.deploymentStatus;
      
      console.log(`[Polling] Agent ${agentId} status: ${deploymentStatus || 'undefined'}`);

      // Call update callback with latest agent data
      if (onUpdate && typeof onUpdate === 'function') {
        onUpdate(agent);
      }

      if (deploymentStatus === "completed") {
        console.log(`[Polling] Deployment completed for agent ${agentId}`);
        return {
          success: true,
          data: {
            status: "completed",
            agentId: agent.agentId,
            message: "Deployment completed successfully"
          }
        };
      }

      if (deploymentStatus === "failed") {
        console.log(`[Polling] Deployment failed for agent ${agentId}`);
        return {
          success: false,
          error: "Deployment failed"
        };
      }

      // If still pending or in_progress, wait and poll again
      if (deploymentStatus === "pending" || deploymentStatus === "in_progress" || !deploymentStatus) {
        if (attempt < maxAttempts - 1) {
          console.log(`[Polling] Status is ${deploymentStatus || 'undefined'}, waiting ${intervalMs}ms before next poll...`);
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
          continue;
        } else {
          console.log(`[Polling] Max attempts reached for agent ${agentId}`);
          return {
            success: false,
            error: "Deployment timed out. It may still be processing."
          };
        }
      }

      // Unknown status
      console.warn(`[Polling] Unknown deployment status: ${deploymentStatus} for agent ${agentId}`);
      return {
        success: false,
        error: `Unknown deployment status: ${deploymentStatus}`
      };
    } catch (error) {
      console.error(`[Polling] Error on attempt ${attempt + 1} for agent ${agentId}:`, error);
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        continue;
      }
      return {
        success: false,
        error: error.message || "Failed to poll agent status"
      };
    }
  }

  console.log(`[Polling] Polling timeout reached for agent ${agentId}`);
  return {
    success: false,
    error: "Polling timeout reached"
  };
};

export const createMantisAgent = async (token, agentData) => {
  try {
    const response = await apiFetch(`${backUrl}/mantis-agents`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(agentData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating mantis agent:", error);
    throw error;
  }
};

export const updateMantisAgent = async (token, agentId, updateData) => {
  try {
    const response = await apiFetch(`${backUrl}/mantis-agents/${agentId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating mantis agent:", error);
    throw error;
  }
};

export const getAgentSessions = async (token, params = {}) => {
  try {
    const qs = new URLSearchParams();
    if (params.page != null) qs.set("page", String(params.page));
    if (params.pageSize != null) qs.set("pageSize", String(params.pageSize));
    if (params.businessName) qs.set("businessName", String(params.businessName));
    if (params.agentName) qs.set("agentName", String(params.agentName));
    if (params.status) qs.set("status", String(params.status));
    if (params.includeEvents) qs.set("includeEvents", "true");
    const q = qs.toString();
    const url = `${backUrl}/agent-sessions${q ? `?${q}` : ""}`;
    const response = await apiFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching agent sessions:", error);
    return { success: false, error: error.message };
  }
};

export const updateAgentSession = async (token, sessionId, updateData) => {
  try {
    const response = await apiFetch(`${backUrl}/agent-sessions/${sessionId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating agent session:", error);
    return { success: false, error: error.message };
  }
};

export const buildAgent = async (managerBaseUrl, agentGCSBucket) => {
  try {
    const buildEndpoint = `${managerBaseUrl}/build`;
    
    const requestBody = {
      project_id: "mantis-387623",
      location: "us-central1",
      bucket: agentGCSBucket
    };

    console.log("Sending build request to:", buildEndpoint);
    console.log("Request body:", requestBody);

    // buildEndpoint is the external agent-manager build service, not our backend —
    // no cookie/CSRF concerns here, plain fetch is correct.
    const response = await fetch(buildEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log("Build response:", result);
    
    return { success: true, data: result };
    
  } catch (error) {
    console.error("Error calling build endpoint:", error);
    return { success: false, error: error.message };
  }
};
